import axios from 'axios'
import api from './api'
import { isTrustedUploadUrl } from '@/utils/sanitizeUrl'
import type { ApiResponse, PaginatedResponse } from '@/types/api.types'
import type {
  EqFileType,
  Project,
  ProjectCostingSummary,
  ProjectFile,
  ProjectFormData,
  ProjectFilters,
  ProjectRequiredFileRow,
} from '@/types/project.types'
import { projectFormToCreatePayload, projectFormToUpdatePayload, projectStatusToApi } from '@/utils/projectForm'

/** FastAPI returns the project dict directly; some routes may use { data: Project } — support both. */
function unwrapProjectPayload(payload: unknown): Project {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    const inner = (payload as ApiResponse<Project>).data
    if (inner !== undefined && inner !== null) return inner as Project
  }
  return payload as Project
}

class ProjectService {
  /** API allows page_size 1–100; values above 100 return 422. */
  static readonly MAX_PAGE_SIZE = 100

  async getProjects(params?: ProjectFilters & { page?: number; page_size?: number }): Promise<PaginatedResponse<Project>> {
    const page_size = Math.min(params?.page_size ?? 10, ProjectService.MAX_PAGE_SIZE)
    const response = await api.get<PaginatedResponse<Project>>('/api/v1/projects/', {
      params: { ...params, page_size },
    })
    return response.data
  }

  /** Fetch all projects by paging (for client-side filters). */
  async fetchAllProjects(filters?: ProjectFilters): Promise<Project[]> {
    const items: Project[] = []
    let page = 1
    const page_size = ProjectService.MAX_PAGE_SIZE
    while (true) {
      const res = await this.getProjects({ ...filters, page, page_size })
      items.push(...res.data)
      if (page >= res.total_pages || res.data.length < page_size) break
      page += 1
    }
    return items
  }

  async getProject(id: string): Promise<Project> {
    const response = await api.get(`/api/v1/projects/${id}`)
    return unwrapProjectPayload(response.data)
  }

  async listEqFileTypes(): Promise<EqFileType[]> {
    const response = await api.get<{ data: EqFileType[] }>('/api/v1/projects/file-types')
    return response.data.data ?? []
  }

  async listRequiredFiles(projectId: string): Promise<ProjectRequiredFileRow[]> {
    const response = await api.get<{ data: ProjectRequiredFileRow[] }>(
      `/api/v1/projects/${projectId}/required-files`
    )
    return response.data.data ?? []
  }

  async addRequiredFile(
    projectId: string,
    fileTypeId: number,
    locationId?: number | null
  ): Promise<ProjectRequiredFileRow> {
    const response = await api.post<ProjectRequiredFileRow>(
      `/api/v1/projects/${projectId}/required-files`,
      { file_type_id: fileTypeId, location_id: locationId ?? null }
    )
    return response.data
  }

  async removeRequiredFile(projectId: string, requirementId: string): Promise<void> {
    await api.delete(`/api/v1/projects/${projectId}/required-files/${requirementId}`)
  }

  async createProject(data: ProjectFormData): Promise<Project> {
    const response = await api.post('/api/v1/projects/', projectFormToCreatePayload(data))
    return unwrapProjectPayload(response.data)
  }

  async updateProject(id: string, data: Partial<ProjectFormData>): Promise<Project> {
    const response = await api.put(`/api/v1/projects/${id}`, projectFormToUpdatePayload(data))
    let project = unwrapProjectPayload(response.data)
    if (data.status) {
      const statusInt = projectStatusToApi(data.status)
      if (statusInt !== undefined) {
        const statusRes = await api.patch(`/api/v1/projects/${id}/status`, { status: statusInt })
        project = unwrapProjectPayload(statusRes.data)
      }
    }
    return project
  }

  async deleteProject(id: string): Promise<void> {
    await api.delete(`/api/v1/projects/${id}`)
  }

  async updateProjectStatus(id: string, status: string): Promise<Project> {
    const statusInt = projectStatusToApi(status)
    if (statusInt === undefined) {
      throw new Error(`Unsupported project status: ${status}`)
    }
    const response = await api.patch(`/api/v1/projects/${id}/status`, { status: statusInt })
    return unwrapProjectPayload(response.data)
  }

  async submitProject(id: string): Promise<Project> {
    const response = await api.post(`/api/v1/projects/${id}/submit`)
    return unwrapProjectPayload(response.data)
  }

  async cancelProject(id: string): Promise<Project> {
    const response = await api.post(`/api/v1/projects/${id}/cancel`)
    return unwrapProjectPayload(response.data)
  }

  async linkContract(projectId: string, contractId: string): Promise<Project> {
    const response = await api.post(`/api/v1/projects/${projectId}/link-contract`, {
      contract_id: contractId,
    })
    return unwrapProjectPayload(response.data)
  }

  async listProjectFiles(projectId: string): Promise<ProjectFile[]> {
    const response = await api.get<{ data: ProjectFile[] }>(`/api/v1/projects/${projectId}/files`)
    return response.data.data ?? []
  }

  /**
   * Uses VITE_CR_FILE_DIRECT_TO_AZURE when true; otherwise multipart via API.
   * fileTypeId = eq_file_types.fty_id (required).
   */
  async uploadProjectFile(
    projectId: string,
    file: File,
    fileTypeId: number,
    onUploadProgress?: (percent: number) => void
  ): Promise<ProjectFile> {
    const useDirectToAzure = import.meta.env.VITE_CR_FILE_DIRECT_TO_AZURE === 'true'
    if (!useDirectToAzure) {
      return this.uploadProjectFileViaApiProxy(projectId, file, fileTypeId, onUploadProgress)
    }

    let direct: { upload_url: string; blob_name: string } | null = null
    try {
      const prep = await api.post<{ upload_url: string; blob_name: string }>(
        `/api/v1/projects/${projectId}/prepare-file-upload`,
        { filename: file.name },
        { timeout: 60000 }
      )
      direct = prep.data
    } catch {
      direct = null
    }

    if (direct?.upload_url && direct?.blob_name) {
      if (!isTrustedUploadUrl(direct.upload_url)) {
        return this.uploadProjectFileViaApiProxy(projectId, file, fileTypeId, onUploadProgress)
      }
      try {
        await axios.put(direct.upload_url, file, {
          timeout: 360000,
          headers: {
            'x-ms-blob-type': 'BlockBlob',
            'Content-Type': file.type || 'application/octet-stream'
          },
          onUploadProgress: onUploadProgress
            ? (e) => {
                const total = e.total ?? 0
                const loaded = e.loaded
                if (total > 0) {
                  onUploadProgress(Math.round((loaded * 100) / total))
                }
              }
            : undefined
        })
      } catch {
        return this.uploadProjectFileViaApiProxy(projectId, file, fileTypeId, onUploadProgress)
      }
      const done = await api.post<ProjectFile>(
        `/api/v1/projects/${projectId}/complete-file-upload`,
        {
          blob_name: direct.blob_name,
          filename: file.name,
          content_type: file.type || 'application/octet-stream',
          file_type_id: fileTypeId
        },
        { timeout: 60000 }
      )
      if (onUploadProgress) onUploadProgress(100)
      return done.data
    }

    return this.uploadProjectFileViaApiProxy(projectId, file, fileTypeId, onUploadProgress)
  }

  private async uploadProjectFileViaApiProxy(
    projectId: string,
    file: File,
    fileTypeId: number,
    onUploadProgress?: (percent: number) => void
  ): Promise<ProjectFile> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('file_type_id', String(fileTypeId))
    const response = await api.post<ProjectFile>(`/api/v1/projects/${projectId}/files`, formData, {
      timeout: 360000,
      transformRequest: [
        (data, headers) => {
          if (data instanceof FormData && headers) {
            delete (headers as Record<string, unknown>)['Content-Type']
            ;(headers as { delete?: (name: string) => void }).delete?.('Content-Type')
          }
          return data
        }
      ],
      onUploadProgress: onUploadProgress
        ? (e) => {
            const total = e.total ?? 0
            const loaded = e.loaded
            if (total > 0) {
              const pct = Math.min(99, Math.round((loaded * 100) / total))
              onUploadProgress(pct)
            } else {
              onUploadProgress(0)
            }
          }
        : undefined
    })
    if (onUploadProgress) onUploadProgress(100)
    return response.data
  }

  async getProjectFileBlob(projectId: string, fileId: string): Promise<Blob> {
    const response = await api.get(`/api/v1/projects/${projectId}/files/${fileId}/download`, {
      responseType: 'blob'
    })
    const contentType = response.headers['content-type']
    return new Blob([response.data], {
      type: typeof contentType === 'string' ? contentType : 'application/octet-stream'
    })
  }

  async downloadProjectFile(projectId: string, fileId: string, fileName: string): Promise<void> {
    const blob = await this.getProjectFileBlob(projectId, fileId)
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', fileName || 'file')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  /** Open PDF or image in a new tab (uses blob URL from authenticated download). */
  async openProjectFileInBrowser(projectId: string, fileId: string): Promise<void> {
    const blob = await this.getProjectFileBlob(projectId, fileId)
    const url = window.URL.createObjectURL(blob)
    const win = window.open(url, '_blank', 'noopener,noreferrer')
    if (!win) {
      window.URL.revokeObjectURL(url)
      throw new Error('Popup blocked')
    }
    setTimeout(() => window.URL.revokeObjectURL(url), 120_000)
  }

  async deleteProjectFile(projectId: string, fileId: string): Promise<void> {
    await api.delete(`/api/v1/projects/${projectId}/files/${fileId}`)
  }

  async getCostingSummary(projectId: string): Promise<ProjectCostingSummary> {
    const response = await api.get<ProjectCostingSummary>(`/api/v1/projects/${projectId}/costing-summary`)
    return response.data
  }
}

export default new ProjectService()
